import { useEffect, useState } from "react";
import { YStack, XStack, Text, Stack, Input } from "tamagui";
import liff from "@line/liff";
import { useOrder } from "@utils/store/cart/useOrder";
import { hasReservableSlots, isSlotSelected } from "@utils/func/deliverySlots";
import { appToast } from "@components/Toast";
import KioskTimePicker from "./KioskTimePicker";
import { kiosk } from "../kioskTheme";
import { SheetShell, Question, BigOption, KioskCTA, KioskBackBtn } from "./KioskParts";
import { useKiosk, KioskInvoiceType } from "../useKiosk";
import { fetchCustomerTaxId, decideTaxIdPrefill } from "@utils/func/customerTaxId";
import { buildXContextHeader } from "@utils/api/headers";
import { useUser } from "@utils/store/user/useUser";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { handleAPIListContextMethods, handleAPIListPaymentMethods, OwnedPaymentMethod } from "@screen/Home/pages/Account/API/handlePaymentAPI";
import { shouldOfferApplePayInLine } from "@utils/applePay";
import PaymentOptions from "./PaymentOptions";
import { useIPassEntry } from "@utils/store/ipassEntry";
import KioskAddCard from "../AddCard";

export type CheckoutField = "time" | "payment" | "invoice";

interface Props {
  /** 從確認頁點「變更」進來：只開單一步驟、按鈕為「完成修改」、完成後回確認頁。 */
  fromConfirm: boolean;
  startField?: CheckoutField;
  onClose: () => void;
  onDone: () => void;
}

const carrierValid = (v: string) => /^\/[0-9A-Z]{7}$/.test(v);
const taxValid = (v: string) => /^\d{8}$/.test(v);

const CheckoutGuide = ({ fromConfirm, startField = "time", onClose, onDone }: Props) => {
  const { deliveryTime, allowReservation, deliverySlots, setDeliveryTime, isOpenNow } = useOrder();
  const { firstCheckoutDone, invoiceType, invoiceCarrier, invoiceTax, setInvoice, showInvoice, isMonthly, selectedPayment, setSelectedPayment, allowElectronicPay } = useKiosk();
  const { cusJwtToken } = useCusAuth();
  // 電子付款開關：supplier platform_setting.enable_electronic_payment（PW 後台控制，bootstrap 帶入）
  const elecOn = allowElectronicPay;
  // Apple Pay 選項顯示條件：電子付款開啟（與信用卡同閘門）且為 iPhone（不要求在 LINE App 內）；
  // 開發環境（devOverride）不受裝置限制，方便非 iPhone 裝置測試。這裡只做標記選取，不呼叫 TapPay。
  const showApplePay =
    elecOn &&
    shouldOfferApplePayInLine({
      os: liff.getOS() ?? null,
      paymentMethod: "apple_pay",
      payable: true,
      devOverride: process.env.NODE_ENV === "development",
    });

  // 只有立即配送（瓦斯行未開放預約或無可預約時段）→ 自動設立即配送、跳過「什麼時候送」步驟
  // 非營業時立即配送不可選，不能強制成立即配送（否則與 KioskTimePicker 落回預約互相覆寫）。
  const onlyImmediate = isOpenNow && (!allowReservation || !hasReservableSlots(deliverySlots));
  useEffect(() => {
    if (onlyImmediate && deliveryTime.type !== "immediate") {
      setDeliveryTime({ type: "immediate", date: null, slotId: null, slotSource: null, slotLabel: null });
    }
  }, [onlyImmediate, deliveryTime.type, setDeliveryTime]);

  // 步驟組成：確認頁變更 → 單步；首次 → 時間+付款+發票；熟客 → 時間+付款（發票沿用上次）
  // 付款方式步驟只在電子付款開啟時出現（關閉時維持原本時間/發票步驟，行為不變）。
  const baseSteps: CheckoutField[] = fromConfirm
    ? [startField]
    : firstCheckoutDone
      ? elecOn
        ? ["time", "payment"]
        : ["time"]
      : elecOn
        ? ["time", "payment", "invoice"]
        : ["time", "invoice"];
  // 從 iPASS MONEY APP 進來：付款方式固定 iPASS Money、不出現「怎麼付款？」步驟
  const ipassLocked = useIPassEntry((s) => s.lockPayment);
  useEffect(() => {
    if (ipassLocked && selectedPayment?.type !== "ipass_money") setSelectedPayment({ type: "ipass_money" });
  }, [ipassLocked, selectedPayment?.type, setSelectedPayment]);
  const lockedSteps = ipassLocked ? baseSteps.filter((f) => f !== "payment") : baseSteps;
  // 平台關閉發票區（show_invoice=false）→ 移除發票步驟。
  // 未開放預約（onlyImmediate）時「時間步驟保留」：頁面照樣出現、只剩「立即配送」可選，
  // 不再整步跳過（2026-07-15 拍板：即使預約關閉也要有顯示立即配送的頁面）。
  const filteredSteps = showInvoice ? lockedSteps : lockedSteps.filter((f) => f !== "invoice");
  const steps: CheckoutField[] = filteredSteps.length > 0 ? filteredSteps : ["time"];
  // 非 fromConfirm 也支援指定起始步驟（確認頁「返回」→ 回到「怎麼付款？」用）；找不到就從頭開始。
  const [step, setStep] = useState(() => (fromConfirm ? 0 : Math.max(0, steps.indexOf(startField))));
  // 新增信用卡以 overlay 疊在步驟上（不換路由）→ ✕ 收起即回到「怎麼付款？」。
  const [showAddCard, setShowAddCard] = useState(false);
  // 綁卡成功後遞增 → 重新載入卡片清單。
  const [cardReload, setCardReload] = useState(0);
  // 引導一開啟就先載卡片（不等進到「怎麼付款？」步驟），進步驟時即有清單、預選也提早完成。
  // 預選順位：帳戶預設付款（context payment-default）→ 清單第一張卡；只打勾、不自動前進。
  const [cards, setCards] = useState<OwnedPaymentMethod[]>([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  useEffect(() => {
    if (!elecOn || !cusJwtToken) return;
    let alive = true;
    setCardsLoading(true);
    const contextId = useUser.getState().chooseIdentity?.context_id;
    Promise.all([
      handleAPIListPaymentMethods(cusJwtToken),
      contextId ? handleAPIListContextMethods(contextId, cusJwtToken) : Promise.resolve(null),
    ])
      .then(([owned, ctxRes]) => {
        if (!alive) return;
        const list = owned.state === "success" ? owned.items.filter((c) => c.method_type === "credit_card") : [];
        setCards(list);
        if (list.length > 0 && !useKiosk.getState().selectedPayment && !useIPassEntry.getState().lockPayment) {
          const defaultId = ctxRes && ctxRes.state === "success" ? ctxRes.defaultId : null;
          const preferred = (defaultId && list.find((c) => c.payment_method_id === defaultId)) || list[0];
          setSelectedPayment({
            type: "credit_card",
            paymentMethodId: preferred.payment_method_id,
            label: `信用卡 •••• ${preferred.card?.lastfour ?? "----"}`,
          });
        }
      })
      .finally(() => {
        if (alive) setCardsLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [elecOn, cusJwtToken, cardReload]);
  const field = steps[step];
  const isLast = step === steps.length - 1;

  // 預約配送要「這個時段此刻真的還能選」才算過關：只看 slotId 有值的話，草稿帶回或 sessionStorage
  // 殘留的舊 slot_id（已不在今次 deliverySlots 裡）會讓畫面沒有任何時段亮著、「下一步」卻可按。
  const timeOk =
    deliveryTime.type === "immediate" || isSlotSelected(deliverySlots, deliveryTime.date, deliveryTime.slotId);
  const invoiceOk =
    invoiceType === "donate" ||
    invoiceType === "other" ||
    (invoiceType === "carrier" && carrierValid(invoiceCarrier)) ||
    (invoiceType === "tax_id" && taxValid(invoiceTax));

  // 付款方式必須主動選定（或由預選帶入）才能前往下一步。
  const paymentOk = !elecOn || !!selectedPayment;
  const fieldOk = field === "time" ? timeOk : field === "invoice" ? invoiceOk : paymentOk;

  // force：選項一點下（或綁卡成功）即前進——此時 state 剛寫入、closure 內的 fieldOk 還是舊值，
  // 用 force 跳過驗證避免被擋。
  const next = (force = false) => {
    if (!fieldOk && !force) {
      appToast.error(field === "time" ? "請選擇配送日期與時段" : field === "payment" ? "請選擇付款方式" : "請完成發票資訊");
      return;
    }
    if (fromConfirm || isLast) {
      onDone();
      return;
    }
    setStep(step + 1);
  };

  const pickInvoice = (t: KioskInvoiceType) => setInvoice({ invoiceType: t });

  // 捐贈選項暫時隱藏（發票捐贈功能尚未生效）：
  // 舊版預設值/persist 殘留的 donate 一律導向免填的「其他」，避免以捐贈送出或卡在載具驗證。
  useEffect(() => {
    if (invoiceType === "donate") setInvoice({ invoiceType: "other" });
  }, [invoiceType, setInvoice]);

  // 統編欄位為空時，自動帶入註冊時留存的統編（使用者仍可修改）。
  useEffect(() => {
    if (!showInvoice || invoiceTax) return;
    let cancelled = false;
    (async () => {
      const registered = await fetchCustomerTaxId(
        buildXContextHeader(useUser.getState().chooseIdentity) || undefined,
        useCusAuth.getState().cusJwtToken || undefined,
      );
      const s = useKiosk.getState();
      const action = decideTaxIdPrefill({
        bootstrapLoaded: true, // CheckoutGuide 掛載時 kiosk bootstrap 必已完成
        showInvoice: s.showInvoice,
        currentType: s.invoiceType,
        currentCarrier: s.invoiceCarrier,
        currentTax: s.invoiceTax,
        registered,
      });
      if (!cancelled && action) {
        // fill-and-select：使用者尚未動過發票區 → 連「統編」類型一起選上，
        // 否則統編輸入框不會顯示、帶入了也看不到。
        setInvoice(
          action === "fill-and-select"
            ? { invoiceType: "tax_id", invoiceTax: registered }
            : { invoiceTax: registered },
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showInvoice]);

  return (
    <>
    <SheetShell
      onClose={onClose}
      dotsTotal={steps.length}
      dotsCurrent={step}
      rightLabel="結帳"
      footer={
        <XStack gap={11}>
          {step > 0 && !fromConfirm && (
            <Stack flexBasis={110} flexGrow={0}>
              <KioskBackBtn label="上一步" onPress={() => setStep(step - 1)} />
            </Stack>
          )}
          <Stack flex={1}>
            <KioskCTA label={fromConfirm ? "完成修改" : isLast ? "前往確認訂單" : "下一步"} onPress={() => next()} disabled={!fieldOk} />
          </Stack>
        </XStack>
      }>
      {field === "time" && (
        <YStack className="kiosk-stepin">
          <Question q="什麼時候送？" sub="選擇配送方式與時段" />
          <KioskTimePicker />
        </YStack>
      )}

      {field === "payment" && (
        <YStack className="kiosk-stepin">
          <Question q="怎麼付款？" sub="選擇付款方式" />
          {/* 點選付款方式即前往下一步（與「什麼時候送」不同：付款方式一按即決定，免再按下一步）。 */}
          <PaymentOptions
            isMonthly={isMonthly}
            selected={selectedPayment}
            onSelect={(v) => {
              setSelectedPayment(v);
              next(true);
            }}
            onAddCard={() => setShowAddCard(true)}
            cards={cards}
            loading={cardsLoading}
            showApplePay={showApplePay}
          />
        </YStack>
      )}

      {field === "invoice" && (
        <YStack className="kiosk-stepin">
          <Question q="發票怎麼開？" sub="選擇發票類型" />
          <XStack gap={9}>
            <Stack flex={1}>
              <BigOption icon="mobile" label="載具" selected={invoiceType === "carrier"} onPress={() => pickInvoice("carrier")} />
            </Stack>
            <Stack flex={1}>
              <BigOption icon="building" label="統編" selected={invoiceType === "tax_id"} onPress={() => pickInvoice("tax_id")} />
            </Stack>
            <Stack flex={1}>
              <BigOption icon="receipt" label="其他" selected={invoiceType === "other"} onPress={() => pickInvoice("other")} />
            </Stack>
            {/* 捐贈選項暫時隱藏（發票捐贈功能尚未生效）：
            <Stack flex={1}>
              <BigOption icon="heart" label="捐贈" selected={invoiceType === "donate"} onPress={() => pickInvoice("donate")} />
            </Stack> */}
          </XStack>

          {invoiceType === "carrier" && (
            <YStack mt={14} backgroundColor="#f4f6fb" borderRadius={14} p={14}>
              <Text fontSize={12} color={kiosk.grey} mb={7}>
                手機條碼載具
              </Text>
              <Input
                value={invoiceCarrier}
                onChangeText={(t: string) => setInvoice({ invoiceCarrier: t.toUpperCase() })}
                maxLength={8}
                placeholder="/ABC1234"
                autoCapitalize="characters"
                borderWidth={1.5}
                borderColor={kiosk.line}
                borderRadius={11}
                fontSize={15}
                h={48}
                backgroundColor="#fff"
                color={kiosk.ink}
              />
              <Text fontSize={11} color={carrierValid(invoiceCarrier) ? kiosk.green : "#9aa3b2"} mt={7}>
                {carrierValid(invoiceCarrier) ? "載具格式正確" : "格式為「/」加 7 碼英數，例：/ABC1234"}
              </Text>
            </YStack>
          )}

          {invoiceType === "tax_id" && (
            <YStack mt={14} backgroundColor="#f4f6fb" borderRadius={14} p={14}>
              <Text fontSize={12} color={kiosk.grey} mb={7}>
                公司統一編號
              </Text>
              <Input
                value={invoiceTax}
                onChangeText={(t: string) => setInvoice({ invoiceTax: t.replace(/\D/g, "").slice(0, 8) })}
                maxLength={8}
                inputMode="numeric"
                placeholder="8 碼數字"
                borderWidth={1.5}
                borderColor={kiosk.line}
                borderRadius={11}
                fontSize={15}
                h={48}
                backgroundColor="#fff"
                color={kiosk.ink}
              />
              <Text fontSize={11} color={taxValid(invoiceTax) ? kiosk.green : "#9aa3b2"} mt={7}>
                {taxValid(invoiceTax) ? "統編格式正確" : "請輸入 8 位數統一編號"}
              </Text>
            </YStack>
          )}

          {/* 捐贈選項暫時隱藏（發票捐贈功能尚未生效）：
          {invoiceType === "donate" && (
            <XStack mt={14} backgroundColor="#f4f6fb" borderRadius={14} p={14} ai="center" gap={9}>
              <Text fontSize={13} color="#46506a" lineHeight={19}>
                發票將自動捐贈給公益團體，您不會收到電子發票。
              </Text>
            </XStack>
          )} */}
        </YStack>
      )}
    </SheetShell>
    {showAddCard && (
      <KioskAddCard
        onClose={() => setShowAddCard(false)}
        onBound={(created) => {
          setCardReload((k) => k + 1);
          // 綁卡成功 → 直接選中新卡並前往下一步（免使用者再點一次）
          if (created?.payment_method_id) {
            setSelectedPayment({
              type: "credit_card",
              paymentMethodId: created.payment_method_id,
              label: `信用卡 •••• ${created.card?.lastfour ?? "----"}`,
            });
            if (field === "payment") next(true);
          }
        }}
        cusJwtToken={cusJwtToken || ""}
      />
    )}
    </>
  );
};

export default CheckoutGuide;
